import { Plus, Search, Tag, X } from "lucide-react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { normalizeMRTag } from "../hooks/useMRTags";
import { Select } from "./Select";

const VIEWPORT_MARGIN = 8;

const tagCompareKey = (tag) => normalizeMRTag(tag).toLowerCase();

export const normalizeTagFilterValue = (value) => {
	const rawValues = Array.isArray(value) ? value : value ? [value] : [];
	const seen = new Set();
	const filters = [];

	for (const rawValue of rawValues) {
		const tag = normalizeMRTag(rawValue);
		const key = tagCompareKey(tag);
		if (!tag || seen.has(key)) continue;
		seen.add(key);
		filters.push(tag);
	}

	return filters;
};

export const mrMatchesActiveTagFilters = (tags = [], filters = []) => {
	const selectedKeys = new Set(
		normalizeTagFilterValue(filters).map((tag) => tagCompareKey(tag)),
	);
	if (selectedKeys.size === 0) return true;

	return tags.some((tag) => selectedKeys.has(tagCompareKey(tag)));
};

const hueForTag = (tag) => {
	let hash = 0;
	for (const char of normalizeMRTag(tag).toLowerCase()) {
		hash = (hash * 31 + char.charCodeAt(0)) % 360;
	}
	return hash;
};

export const tagStyleFor = (tag) => ({
	"--mr-tag-hue": hueForTag(tag),
});

export const TagPill = ({ tag, onRemove, className = "" }) => (
	<span
		className={`mr-tag-pill inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-5 ${className}`}
		style={tagStyleFor(tag)}
		title={tag}
	>
		<span className="truncate">{tag}</span>
		{onRemove && (
			<button
				type="button"
				onClick={(event) => {
					event.stopPropagation();
					onRemove(tag);
				}}
				className="no-hover-lift -mr-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-current"
				aria-label={`Remove tag ${tag}`}
			>
				<X className="h-3 w-3" />
			</button>
		)}
	</span>
);

export const MRTagsEditor = ({
	mr,
	tags = [],
	allTags = [],
	onAddTag,
	onRemoveTag,
	className = "",
	compact = false,
}) => {
	const [open, setOpen] = useState(false);
	const [input, setInput] = useState("");
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const ref = useRef(null);
	const menuRef = useRef(null);
	const inputRef = useRef(null);
	const tagKeys = useMemo(
		() => new Set(tags.map((tag) => tagCompareKey(tag))),
		[tags],
	);
	const suggestions = useMemo(() => {
		const query = tagCompareKey(input);
		return allTags
			.filter((tag) => !tagKeys.has(tagCompareKey(tag)))
			.filter((tag) => !query || tagCompareKey(tag).includes(query))
			.slice(0, 6);
	}, [allTags, input, tagKeys]);

	const updatePosition = useCallback(() => {
		const trigger = ref.current;
		if (!trigger) return;

		const rect = trigger.getBoundingClientRect();
		const menuWidth = menuRef.current?.offsetWidth || 256;
		const top = rect.bottom + 6;
		const left = Math.min(
			Math.max(rect.right - menuWidth, VIEWPORT_MARGIN),
			window.innerWidth - VIEWPORT_MARGIN - menuWidth,
		);
		setPosition({ top, left });
	}, []);

	useEffect(() => {
		if (!open) return undefined;

		updatePosition();
		const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);
		const handlePointerDown = (event) => {
			if (menuRef.current?.contains(event.target)) return;
			if (ref.current?.contains(event.target)) return;
			setOpen(false);
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setOpen(false);
		};
		const handleReposition = () => updatePosition();

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		window.addEventListener("resize", handleReposition);
		document.addEventListener("scroll", handleReposition, true);
		return () => {
			clearTimeout(focusTimer);
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("resize", handleReposition);
			document.removeEventListener("scroll", handleReposition, true);
		};
	}, [open, updatePosition]);

	const submitTag = (rawTag = input) => {
		const tag = normalizeMRTag(rawTag);
		if (!tag || tagKeys.has(tagCompareKey(tag))) return;

		onAddTag?.(mr, tag);
		setInput("");
		setOpen(false);
	};

	const menu =
		open &&
		createPortal(
			<div
				ref={menuRef}
				className="fixed z-[10000] w-64 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-2 shadow-xl"
				style={{ top: position.top, left: position.left }}
				onMouseDown={(event) => event.stopPropagation()}
			>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						submitTag();
					}}
					className="flex items-center gap-2"
				>
					<div className="relative min-w-0 flex-1">
						<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
						<input
							ref={inputRef}
							type="text"
							value={input}
							onChange={(event) => setInput(event.target.value)}
							placeholder="Add or search tags"
							className="h-8 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] pl-8 pr-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						/>
					</div>
					<button
						type="submit"
						disabled={
							!normalizeMRTag(input) || tagKeys.has(tagCompareKey(input))
						}
						className="no-hover-lift inline-flex h-8 shrink-0 items-center rounded-md bg-[var(--color-action)] px-2.5 text-xs font-medium text-[var(--color-action-text)] hover:bg-[var(--color-action-hover)] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
					>
						Add
					</button>
				</form>

				{suggestions.length > 0 && (
					<div className="mt-2 max-h-48 overflow-auto">
						{suggestions.map((tag) => (
							<button
								key={tagCompareKey(tag)}
								type="button"
								onClick={() => submitTag(tag)}
								className="no-hover-lift flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
							>
								<TagPill tag={tag} className="max-w-[12rem]" />
							</button>
						))}
					</div>
				)}
			</div>,
			document.body,
		);

	return (
		<div
			ref={ref}
			data-no-row-select
			onClick={(event) => event.stopPropagation()}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") event.stopPropagation();
			}}
			onMouseDown={(event) => event.stopPropagation()}
			className={`relative flex min-w-0 flex-wrap items-center gap-1 ${className}`}
		>
			{tags.map((tag) => (
				<TagPill
					key={tagCompareKey(tag)}
					tag={tag}
					onRemove={onRemoveTag ? () => onRemoveTag(mr, tag) : undefined}
					className={compact ? "max-w-[7rem]" : "max-w-[9rem]"}
				/>
			))}

			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				className={`no-hover-lift inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] ${
					compact ? "h-6 px-1.5 text-[11px]" : "h-7 px-2 text-xs font-medium"
				}`}
				aria-expanded={open}
				aria-label={`Add tag to !${mr?.iid || "merge request"}`}
				title="Add tag"
			>
				<Plus className="h-3.5 w-3.5" />
				{!compact && "Tag"}
			</button>
			{menu}
		</div>
	);
};

export const MRTagDialog = ({
	open,
	mrs = [],
	allTags = [],
	onClose,
	onSubmit,
}) => {
	const [input, setInput] = useState("");
	const inputRef = useRef(null);
	const normalizedInput = normalizeMRTag(input);
	const suggestions = useMemo(() => {
		const query = tagCompareKey(input);
		return allTags
			.filter((tag) => !query || tagCompareKey(tag).includes(query))
			.slice(0, 8);
	}, [allTags, input]);

	useEffect(() => {
		if (!open) return undefined;

		setInput("");
		const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);
		const handleKeyDown = (event) => {
			if (event.key === "Escape") onClose?.();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			clearTimeout(focusTimer);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [onClose, open]);

	if (!open) return null;

	const submit = (rawTag = input) => {
		const tag = normalizeMRTag(rawTag);
		if (!tag) return;
		onSubmit?.(tag, mrs);
		setInput("");
		onClose?.();
	};

	return (
		<dialog
			open
			className="fixed inset-0 z-[10000] m-0 flex h-screen max-h-none w-screen max-w-none items-center justify-center bg-black/60 px-4 text-left"
			aria-modal="true"
			aria-labelledby="mr-tag-dialog-title"
			onMouseDown={onClose}
		>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					submit();
				}}
				className="w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-4 shadow-xl"
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className="mb-3 flex items-center justify-between gap-3">
					<h2
						id="mr-tag-dialog-title"
						className="text-base font-semibold text-[var(--color-text-primary)]"
					>
						Add tag to {mrs.length > 1 ? `${mrs.length} MRs` : "MR"}
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="no-hover-lift rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label="Close add tag dialog"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<div className="relative">
					<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
					<input
						ref={inputRef}
						type="text"
						value={input}
						onChange={(event) => setInput(event.target.value)}
						placeholder="Add or search tags"
						className="h-9 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] pl-9 pr-3 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
					/>
				</div>

				{suggestions.length > 0 && (
					<div className="mt-3 flex max-h-44 flex-wrap gap-1.5 overflow-auto">
						{suggestions.map((tag) => (
							<button
								key={tagCompareKey(tag)}
								type="button"
								onClick={() => submit(tag)}
								className="no-hover-lift"
							>
								<TagPill tag={tag} />
							</button>
						))}
					</div>
				)}

				<div className="mt-4 flex justify-end gap-2">
					<button
						type="button"
						onClick={onClose}
						className="no-hover-lift rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)]"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={!normalizedInput}
						className="no-hover-lift rounded-md bg-[var(--color-action)] px-3 py-2 text-sm font-medium text-[var(--color-action-text)] disabled:cursor-not-allowed disabled:opacity-60"
					>
						Add tag
					</button>
				</div>
			</form>
		</dialog>
	);
};

export const TagQuickFilter = ({
	tags = [],
	value = [],
	onChange,
	className = "",
	style,
}) => {
	const options = useMemo(() => {
		const tagsByKey = new Map(
			tags.map((tag) => [tagCompareKey(tag), normalizeMRTag(tag)]),
		);
		const selected = normalizeTagFilterValue(value).map((tag) => {
			const key = tagCompareKey(tag);
			return tagsByKey.get(key) || tag;
		});
		const tagOptions = tags.map((tag) => ({ value: tag, label: tag }));

		for (let index = selected.length - 1; index >= 0; index -= 1) {
			const tag = selected[index];
			if (tagsByKey.has(tagCompareKey(tag))) continue;
			tagOptions.unshift({ value: tag, label: tag });
		}

		return [
			{ value: "", label: tags.length ? "All tags" : "No tags" },
			...tagOptions,
		];
	}, [tags, value]);
	const selectedValues = useMemo(() => {
		const optionsByKey = new Map(
			options.map((option) => [tagCompareKey(option.value), option.value]),
		);
		return normalizeTagFilterValue(value).map((tag) => {
			const key = tagCompareKey(tag);
			return optionsByKey.get(key) || tag;
		});
	}, [options, value]);

	return (
		<Select
			value={selectedValues}
			onChange={onChange}
			options={options}
			placeholder={tags.length ? "All tags" : "No tags"}
			icon={Tag}
			ariaLabel="Filter merge requests by tag"
			align="right"
			className={`w-[11.5rem] max-w-full ${className}`}
			style={style}
			multiple
			multipleLabel={(selectedOptions) =>
				selectedOptions.length === 1
					? selectedOptions[0].label
					: selectedOptions.length > 1
						? `${selectedOptions.length} tags`
						: tags.length
							? "All tags"
							: "No tags"
			}
		/>
	);
};
